import dayjs from "dayjs";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Modal,
  Select,
  Text,
  Textarea,
  TextInput,
  Tooltip
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import AppPagination from "@app/common/components/AppPagination";
import { confirmNotification, warningNotification } from "@app/common/components/notifications";
import { FilterSearchButton } from "@app/common/components/SearchButtonFilters";
import { Template } from "@app/common/components/Template";
import { TextField } from "@app/common/components/TextField";
import { formattedDataSelect } from "@app/common/utils/utils";

import { useState } from "react";
import { useGetCancellationReasons, useGetStatusList } from "@app/services/common/hooks";
import {
  useGetAnnulDeliveryRequests,
  usePatchAnnulDeliveryRequest
} from "@app/services/cancellationDeliveries/hooks";
import { AnnulDeliveryRequest } from "@app/services/cancellationDeliveries/types";
import { usePermissions } from "@app/common/hooks/usePermissions";
import {
  RBAC_ACTIONS,
  RBAC_LABELS,
  MODULE_PERMISSION_PREFIX
} from "@app/common/utils/permissionsConstants";

const TODAY = dayjs().format("YYYY-MM-DD");

const CancellationDeliveries = () => {
  const { can } = usePermissions();
  const canApprove = can(RBAC_ACTIONS.APPROVE, MODULE_PERMISSION_PREFIX.CANCELLATION_DELIVERIES);
  const canReject = can(RBAC_ACTIONS.REJECT, MODULE_PERMISSION_PREFIX.CANCELLATION_DELIVERIES);

  const [filters, setFilters] = useState({
    sta_id: "" as string,
    date_from: TODAY,
    date_to: TODAY,
    search: ""
  });
  const [dateFilters, setDateFilters] = useState({
    date_from: TODAY,
    date_to: TODAY
  });
  const [page, setPage] = useState<number>(1);
  const [rejectModal, setRejectModal] = useState<{
    isOpen: boolean;
    radId: number | null;
    attributableTo: string | null;
    radRejectReasonId: string | null;
    radRejectReasonText: string;
  }>({
    isOpen: false,
    radId: null,
    attributableTo: null,
    radRejectReasonId: null,
    radRejectReasonText: ""
  });

  const { data: requestList, rowTotal } = useGetAnnulDeliveryRequests({
    ...filters,
    sta_id: filters.sta_id || undefined,
    page,
    perpage: 10
  });

  const { data: statusListData } = useGetStatusList({
    staFunctionality: "annul_delivery"
  });

  const { mutateAsync: patchRequest, isPending } = usePatchAnnulDeliveryRequest(async () => {
    setRejectModal({
      isOpen: false,
      radId: null,
      attributableTo: null,
      radRejectReasonId: null,
      radRejectReasonText: ""
    });
  });

  const { data: cancellationReasons } = useGetCancellationReasons(
    rejectModal.attributableTo ?? "",
    !!rejectModal.attributableTo
  );

  const handleSubmitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);

    const sta_id = formData.get("status")?.toString() || "";
    const date_from = formData.get("date-start")?.toString() || "";
    const date_to = formData.get("date-end")?.toString() || "";
    const search = formData.get("search")?.toString() || "";

    setFilters({ sta_id, date_from, date_to, search });
    setPage(1);
  };

  const handleApprove = (radId: number) => {
    if (!canApprove) {
      return;
    }
    confirmNotification(
      "Se autorizará la anulación de esta entrega. El dinero recaudado será trasladado al bolsillo del paciente.",
      "¿Está seguro?",
      async () => {
        await patchRequest({ radId, payload: { approved: 1 } });
      }
    );
  };

  const handleReject = () => {
    if (!canReject) {
      return;
    }
    if (
      !rejectModal.radId ||
      !rejectModal.radRejectReasonId ||
      !rejectModal.radRejectReasonText.trim()
    ) {
      warningNotification("Debe completar todos los campos obligatorios");
      return;
    }
    patchRequest({
      radId: rejectModal.radId,
      payload: {
        approved: 0,
        radRejectReasonId: Number(rejectModal.radRejectReasonId),
        radRejectReasonText: rejectModal.radRejectReasonText
      }
    });
  };

  const renderFiltersHeader = () => (
    <Grid
      align="flex-end"
      mb="lg"
      w="100%"
      columns={12}
      data-testid="cancellationdeliveries-filters"
    >
      <Grid.Col span={2}>
        <Select
          data-testid="cancellationdeliveries-status-select"
          label="Estado"
          data={formattedDataSelect(statusListData?.results ?? [], "description", "id")}
          searchable
          placeholder="Seleccionar..."
          name="status"
        />
      </Grid.Col>
      <Grid.Col span={2}>
        <TextInput
          data-testid="cancellationdeliveries-start-date-input"
          label="Fecha desde"
          type="date"
          max={TODAY}
          placeholder="Escribir..."
          name="date-start"
          value={dateFilters.date_from}
          onChange={({ target }) => setDateFilters((s) => ({ ...s, date_from: target.value }))}
        />
      </Grid.Col>
      <Grid.Col span={2}>
        <TextInput
          data-testid="cancellationdeliveries-end-date-input"
          label="Fecha hasta"
          type="date"
          min={dateFilters.date_from}
          max={TODAY}
          placeholder="Escribir..."
          name="date-end"
          value={dateFilters.date_to}
          onChange={({ target }) => setDateFilters((s) => ({ ...s, date_to: target.value }))}
        />
      </Grid.Col>
      <Grid.Col span={3}>
        <TextField
          data-testid="cancellationdeliveries-search-input"
          placeholder="Buscar por nombre del tercero..."
          variant="filled"
          className="flex-grow-1 me-4"
          name="search"
        />
      </Grid.Col>
      <Grid.Col span={2}>
        <FilterSearchButton data-testid="cancellationdeliveries-search-button" />
      </Grid.Col>
    </Grid>
  );

  const renderCard = (item: AnnulDeliveryRequest, index: number) => {
    const isStatusPending = item.status.value === "pending";
    const lastUpdate = item.last_update_at
      ? dayjs(item.last_update_at).format("DD/MM/YYYY hh:mm A")
      : "-";

    return (
      <Card
        key={item.rad_id}
        withBorder
        shadow="sm"
        radius="md"
        mb="md"
        p="md"
        data-testid={`cancellationdeliveries-card-${index}`}
      >
        <Flex justify="space-between" align="center" mb="sm">
          <Flex gap="xl" wrap="wrap" align="center">
            <Text size="sm">
              <Text span fw={600} className="primary-color">
                Solicitud:{" "}
              </Text>
              {item.rad_reason_text || "-"}
            </Text>
            <Text size="sm">
              <Text span fw={600} className="primary-color">
                F. de solicitud:{" "}
              </Text>
              {item.created_at ? dayjs(item.created_at).format("YYYY-MM-DD") : "-"}
            </Text>
          </Flex>

          <Flex gap="xs" align="center">
            <Badge
              color={item.status.bgColor || "gray"}
              style={{ color: item.status.fontColor }}
              variant="light"
              data-testid={`cancellationdeliveries-status-badge-${index}`}
            >
              {item.status.description}
            </Badge>
            {isStatusPending && (
              <>
                <Tooltip
                  label={canReject ? "Rechazar anulación" : RBAC_LABELS.NO_PERMISSION}
                  withArrow
                >
                  <ActionIcon
                    variant="outline"
                    color="red"
                    radius="xl"
                    size="lg"
                    disabled={!canReject}
                    className={!canReject ? "noPermissionDisabled" : ""}
                    data-testid={`cancellationdeliveries-reject-button-${index}`}
                    onClick={() =>
                      setRejectModal({
                        isOpen: true,
                        radId: item.rad_id,
                        attributableTo: null,
                        radRejectReasonId: null,
                        radRejectReasonText: ""
                      })
                    }
                  >
                    <IconX size={18} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip
                  label={canApprove ? "Autorizar anulación" : RBAC_LABELS.NO_PERMISSION}
                  withArrow
                >
                  <ActionIcon
                    variant="outline"
                    color="green"
                    radius="xl"
                    size="lg"
                    disabled={!canApprove}
                    className={!canApprove ? "noPermissionDisabled" : ""}
                    data-testid={`cancellationdeliveries-approve-button-${index}`}
                    onClick={() => handleApprove(item.rad_id)}
                  >
                    <IconCheck size={18} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Flex>
        </Flex>

        <Grid columns={12} mt="xs">
          <Grid.Col span={3}>
            <Text size="xs" fw={600} className="primary-color">
              Tipo documento
            </Text>
            <Text size="sm">{item.patient.documentType || "-"}</Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" fw={600} className="primary-color">
              Identificación
            </Text>
            <Text size="sm">{item.patient.document || "-"}</Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" fw={600} className="primary-color">
              Tercero
            </Text>
            <Text size="sm">{item.patient.name || "-"}</Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="xs" fw={600} className="primary-color">
              Última actualización
            </Text>
            <Text size="sm">{lastUpdate}</Text>
          </Grid.Col>
        </Grid>
      </Card>
    );
  };

  const renderCards = () => (
    <Box data-testid="cancellationdeliveries-cards-container">
      {requestList.length ? (
        requestList.map(renderCard)
      ) : (
        <Text ta="center" py="xl" c="dimmed" data-testid="cancellationdeliveries-no-data">
          No hay información para mostrar.
        </Text>
      )}
    </Box>
  );

  const renderRejectModal = () => (
    <Modal
      opened={rejectModal.isOpen}
      onClose={() =>
        setRejectModal({
          isOpen: false,
          radId: null,
          attributableTo: null,
          radRejectReasonId: null,
          radRejectReasonText: ""
        })
      }
      title="Rechazar solicitud"
      size="md"
      data-testid="cancellationdeliveries-reject-modal"
    >
      <Box
        className="flex flex-column gap-3"
        data-testid="cancellationdeliveries-reject-modal-content"
      >
        <Select
          label="Atribuible a"
          placeholder="Seleccionar..."
          withAsterisk
          data={[
            { value: "institution", label: "Institución" },
            { value: "patient", label: "Paciente" }
          ]}
          value={rejectModal.attributableTo}
          data-testid="cancellationdeliveries-reject-attributable-to"
          onChange={(val) =>
            setRejectModal((s) => ({ ...s, attributableTo: val, radRejectReasonId: null }))
          }
        />
        <Select
          label="Motivo"
          placeholder="Seleccionar..."
          searchable
          withAsterisk
          disabled={!rejectModal.attributableTo}
          data={formattedDataSelect(cancellationReasons, "canDescription", "canId")}
          value={rejectModal.radRejectReasonId}
          data-testid="cancellationdeliveries-reject-reason-select"
          onChange={(val) => setRejectModal((s) => ({ ...s, radRejectReasonId: val }))}
        />
        <Textarea
          label="Observaciones"
          placeholder="Escribir..."
          withAsterisk
          rows={4}
          value={rejectModal.radRejectReasonText}
          data-testid="cancellationdeliveries-reject-reason-text"
          onChange={({ target }) =>
            setRejectModal((s) => ({ ...s, radRejectReasonText: target.value }))
          }
        />
        <Box className="flex justify-end gap-3" pt="sm">
          <Button
            variant="outline"
            data-testid="cancellationdeliveries-reject-cancel-button"
            onClick={() =>
              setRejectModal({
                isOpen: false,
                radId: null,
                attributableTo: null,
                radRejectReasonId: null,
                radRejectReasonText: ""
              })
            }
          >
            Cancelar
          </Button>
          <Button
            variant="filled"
            loading={isPending}
            disabled={
              !rejectModal.attributableTo ||
              !rejectModal.radRejectReasonId ||
              !rejectModal.radRejectReasonText.trim()
            }
            data-testid="cancellationdeliveries-reject-confirm-button"
            onClick={handleReject}
          >
            Confirmar rechazo
          </Button>
        </Box>
      </Box>
    </Modal>
  );

  return (
    <Template
      title="Solicitudes de anulación de entregas"
      data-testid="cancellationdeliveries-template"
    >
      {renderRejectModal()}
      <form onSubmit={handleSubmitFilters} data-testid="cancellationdeliveries-filters-form">
        {renderFiltersHeader()}
      </form>

      {renderCards()}

      <AppPagination
        data-testid="cancellationdeliveries-pagination"
        total={rowTotal}
        value={page}
        withDetails
        onChange={(value) => setPage(value)}
      />
    </Template>
  );
};

export default CancellationDeliveries;
